import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const ImageGenerationContext = createContext(null);

// Toast informativo cuando un batch IA termina con fallos (post auto-refund).
// Avoids silent failures: if Gemini was rate-limited (RESOURCE_EXHAUSTED) and
// some tasks failed even after retry+fallback, the user sees what happened
// and that their credits were refunded automatically.
const TYPE_LABEL = { recipes: "recetas", images: "imágenes", allergens: "alérgenos" };

async function notifyBatchSummary(restaurantId, type) {
  try {
    const r = await api.request(`/restaurants/${restaurantId}/ai/last-batch-summary?type=${type}`);
    const b = r?.batch;
    if (!b || (b.failed || 0) === 0) return;
    const label = TYPE_LABEL[type] || type;
    const refunded = b.credits_auto_refunded || 0;
    const refundedLine = refunded > 0
      ? ` Hemos devuelto ${refunded} crédito${refunded === 1 ? "" : "s"} automáticamente.`
      : "";
    toast.warning(
      `${b.failed} de ${b.total} ${label} fallaron por saturación temporal del proveedor IA.${refundedLine} Puedes reintentarlas cuando quieras.`,
      { duration: 9000 },
    );
  } catch { /* fail-silent — toast is a courtesy */ }
}



export function ImageGenerationProvider({ children }) {
  const [imgGenActive, setImgGenActive] = useState(false);
  const [recipeGenActive, setRecipeGenActive] = useState(false);
  const [allergenGenActive, setAllergenGenActive] = useState(false);
  const [ingredientEnrichActive, setIngredientEnrichActive] = useState(false);
  const [imgTaskCount, setImgTaskCount] = useState(0);
  const [recipeTaskCount, setRecipeTaskCount] = useState(0);
  const [allergenTaskCount, setAllergenTaskCount] = useState(0);
  const [ingredientEnrichCount, setIngredientEnrichCount] = useState(0);
  const [restaurantBgPhotos, setRestaurantBgPhotos] = useState([]);
  const [currentRestaurantId, setCurrentRestaurantId] = useState(null);

  // ── Batch progress tracking (live X/Y + ETA) ──────────────────────────
  // { images?: {batch_id, total, done, failed, started_at, label},
  //   recipes?: {...}, allergens?: {...}, ingredients?: {...} }
  const [activeBatches, setActiveBatches] = useState({});

  // Register a newly-enqueued batch so the progress dock can show it.
  const registerBatch = useCallback((kind, info) => {
    if (!info?.batch_id) return;
    setActiveBatches(prev => ({
      ...prev,
      [kind]: {
        batch_id: info.batch_id,
        total: info.total || 0,
        done: 0,
        failed: 0,
        started_at: Date.now(),
        label: info.label || kind,
      },
    }));
  }, []);

  // Dismiss a batch card from the dock (user-initiated close).
  const dismissBatch = useCallback((kind) => {
    setActiveBatches(prev => {
      const next = { ...prev };
      delete next[kind];
      return next;
    });
  }, []);

  // Callbacks that external components can register to be called when a batch completes
  const imgBatchDoneCallback = useRef(null);
  const recipeBatchDoneCallback = useRef(null);
  const enrichBatchDoneCallback = useRef(null);

  // Track previous active states for batch-done detection
  const prevImg = useRef(false);
  const prevRecipe = useRef(false);
  const prevAllergen = useRef(false);
  const prevEnrich = useRef(false);

  // ── Poll backend for active AI tasks ─────────────────────────────────
  useEffect(() => {
    if (!currentRestaurantId) return;
    let cancelled = false;

    const poll = async () => {
      try {
        const data = await api.get(`/restaurants/${currentRestaurantId}/ai/active-tasks`);
        if (cancelled) return;
        const imgActive = (data.images || 0) > 0;
        const recActive = (data.recipes || 0) > 0;
        const algActive = (data.allergens || 0) > 0;
        const enrActive = (data.ingredients || 0) > 0;
        setImgTaskCount(data.images || 0);
        setRecipeTaskCount(data.recipes || 0);
        setAllergenTaskCount(data.allergens || 0);
        setIngredientEnrichCount(data.ingredients || 0);
        setImgGenActive(prev => prev || imgActive);
        setRecipeGenActive(prev => prev || recActive);
        setAllergenGenActive(prev => prev || algActive);
        setIngredientEnrichActive(prev => prev || enrActive);

        // Detect transitions active → inactive (batch done)
        if (prevImg.current && !imgActive) {
          setImgGenActive(false);
          imgBatchDoneCallback.current?.();
          notifyBatchSummary(currentRestaurantId, "images");
        }
        if (prevRecipe.current && !recActive) {
          setRecipeGenActive(false);
          recipeBatchDoneCallback.current?.();
          notifyBatchSummary(currentRestaurantId, "recipes");
        }
        if (prevAllergen.current && !algActive) {
          setAllergenGenActive(false);
          notifyBatchSummary(currentRestaurantId, "allergens");
        }
        if (prevEnrich.current && !enrActive) {
          setIngredientEnrichActive(false);
          enrichBatchDoneCallback.current?.();
        }
        // If polling finds no active tasks, clear the flag
        if (!imgActive) setImgGenActive(false);
        if (!recActive) setRecipeGenActive(false);
        if (!algActive) setAllergenGenActive(false);
        if (!enrActive) setIngredientEnrichActive(false);

        prevImg.current = imgActive;
        prevRecipe.current = recActive;
        prevAllergen.current = algActive;
        prevEnrich.current = enrActive;

        // ── Live batch progress: fetch status for each registered batch ──
        // Cheap: one /api/.../ai/batch-status/{bid} call per active batch.
        // Also garbage-collect batches that completed so the dock closes.
        setActiveBatches(prev => {
          const kinds = Object.keys(prev);
          if (kinds.length === 0) return prev;
          // Fire all requests in parallel, don't block the poll cycle.
          Promise.all(
            kinds.map(async (kind) => {
              const b = prev[kind];
              if (!b?.batch_id) return [kind, null];
              try {
                const s = await api.request(
                  `/restaurants/${currentRestaurantId}/ai/batch-status/${b.batch_id}`,
                );
                return [kind, {
                  ...b,
                  total: s.total ?? b.total,
                  done: s.done ?? b.done,
                  failed: s.failed ?? b.failed,
                  status: s.status,
                  completed_at: s.completed_at || null,
                }];
              } catch {
                return [kind, b];
              }
            }),
          ).then((updates) => {
            if (cancelled) return;
            setActiveBatches((curr) => {
              const next = { ...curr };
              let changed = false;
              for (const [kind, val] of updates) {
                if (!val) continue;
                const isComplete =
                  val.status === "completed" ||
                  (val.total > 0 && val.done + val.failed >= val.total);
                if (isComplete) {
                  // Keep on screen for 6s so user can see the final state
                  // then drop automatically.
                  if (!next[kind]?.dropAt) {
                    next[kind] = { ...val, dropAt: Date.now() + 6000 };
                    changed = true;
                  } else if (Date.now() >= next[kind].dropAt) {
                    delete next[kind];
                    changed = true;
                  }
                } else if (
                  next[kind]?.done !== val.done ||
                  next[kind]?.failed !== val.failed ||
                  next[kind]?.total !== val.total
                ) {
                  next[kind] = val;
                  changed = true;
                }
              }
              return changed ? next : curr;
            });
          }).catch(() => { /* silent */ });
          return prev;
        });
      } catch { /* silently ignore */ }
    };

    poll();
    const iv = setInterval(poll, 5000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [currentRestaurantId]);

  const loadAllDishes = async (currentRestaurant) => {
    const menus = await api.getMenus(currentRestaurant.restaurant_id);
    let all = [];
    for (const menu of menus) {
      const menuDishes = await api.getDishes(menu.menu_id);
      all.push(...menuDishes.map(d => ({ ...d, cuisine_type: currentRestaurant.cuisine_type || "" })));
    }
    return all;
  };

  // ── Image generation (fire & forget — notification on completion) ────
  const handleGenerateDishImages = useCallback(async (currentRestaurant, bgPhotos = [], dishIds = null) => {
    if (!currentRestaurant || imgGenActive) return;
    setImgGenActive(true);

    let allDishes = [];
    try {
      allDishes = await loadAllDishes(currentRestaurant);
    } catch {
      toast.error("Error al obtener platos");
      setImgGenActive(false);
      return;
    }

    if (dishIds && dishIds.length > 0) {
      allDishes = allDishes.filter(d => dishIds.includes(d.dish_id));
    }

    if (allDishes.length === 0) {
      toast.error("No hay platos para generar imágenes");
      setImgGenActive(false);
      return;
    }

    const bgUrls = bgPhotos.filter(u => typeof u === "string" && u.length > 0);

    try {
      const result = await api.generateImagesBatch(
        currentRestaurant.restaurant_id,
        allDishes.map(d => d.dish_id),
        currentRestaurant.cuisine_type || "",
        bgUrls
      );
      const queued = result.queued_ai ?? result.queued ?? allDishes.length;
      const stock = result.stock_assigned ?? 0;
      const skipped = result.skipped_existing ?? 0;
      // Register batch for live progress tracking if we got a batch_id
      if (result.batch_id && queued > 0) {
        registerBatch("images", {
          batch_id: result.batch_id,
          total: queued,
          label: "Imágenes",
        });
      }
      const parts = [];
      if (queued > 0) parts.push(`${queued} en cola IA`);
      if (stock > 0) parts.push(`${stock} con foto stock`);
      if (skipped > 0) parts.push(`${skipped} ya tenian imagen`);
      toast.success(
        parts.length > 0
          ? parts.join(", ") + ". Recibiras una notificacion cuando terminen."
          : "No hay imagenes pendientes de generar.",
        { duration: 5000 }
      );
      // Keep imgGenActive=true — the polling will clear it when tasks finish
    } catch (err) {
      const msg = err.message || "";
      if (msg.includes("creditos") || msg.includes("Creditos") || msg.includes("credits") || msg.includes("402")) {
        toast.error(msg || "No tienes suficientes creditos IA. Ve a Suscripcion para recargar.", { duration: 6000 });
      } else {
        toast.error("Error al encolar imagenes: " + msg);
      }
      setImgGenActive(false);
    }
  }, [imgGenActive, registerBatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Recipe generation (fire & forget — notification on completion) ───
  const handleGenerateRecipes = useCallback(async (currentRestaurant, dishIds = null, { mode = "simple" } = {}) => {
    if (!currentRestaurant || recipeGenActive) return;
    setRecipeGenActive(true);

    let allDishes = [];
    try {
      allDishes = await loadAllDishes(currentRestaurant);
    } catch {
      toast.error("Error al obtener platos");
      setRecipeGenActive(false);
      return;
    }

    if (dishIds && dishIds.length > 0) {
      allDishes = allDishes.filter(d => dishIds.includes(d.dish_id));
    }

    allDishes = allDishes.filter(d => !d.is_commercial_beverage);

    if (allDishes.length === 0) {
      toast.error("No hay platos para generar recetas");
      setRecipeGenActive(false);
      return;
    }

    try {
      const apiCall = mode === "professional"
        ? api.generateRecipesProfessionalBulk.bind(api)
        : api.generateRecipesBatch.bind(api);
      const result = await apiCall(
        currentRestaurant.restaurant_id,
        allDishes.map(d => d.dish_id)
      );
      const queued = result.queued ?? 0;
      const skippedExisting = result.skipped_existing ?? 0;
      const skippedBev = result.skipped_beverages ?? 0;
      // Register batch for live progress tracking
      if (result.batch_id && queued > 0) {
        registerBatch("recipes", {
          batch_id: result.batch_id,
          total: queued,
          label: mode === "professional" ? "Recetas profesionales" : "Recetas",
        });
      }
      const parts = [];
      if (queued > 0) {
        parts.push(mode === "professional"
          ? `${queued} en cola IA (modo profesional)`
          : `${queued} en cola IA`);
      }
      if (skippedExisting > 0) parts.push(`${skippedExisting} ya tenian receta`);
      if (skippedBev > 0) parts.push(`${skippedBev} bebidas omitidas`);
      toast.success(
        parts.length > 0
          ? parts.join(", ") + ". Recibiras una notificacion cuando terminen."
          : "No hay recetas pendientes de generar.",
        { duration: 5000 }
      );
      // Keep recipeGenActive=true — the polling will clear it when tasks finish
    } catch (err) {
      const msg = err.message || "";
      if (msg.includes("creditos") || msg.includes("Creditos") || msg.includes("credits") || msg.includes("402")) {
        toast.error(msg || "No tienes suficientes creditos IA. Ve a Suscripcion para recargar.", { duration: 6000 });
      } else {
        toast.error("Error al encolar recetas: " + msg);
      }
      setRecipeGenActive(false);
    }
  }, [recipeGenActive, registerBatch]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ImageGenerationContext.Provider value={{
      imgGenActive, recipeGenActive, allergenGenActive, ingredientEnrichActive,
      imgTaskCount, recipeTaskCount, allergenTaskCount, ingredientEnrichCount,
      restaurantBgPhotos, setRestaurantBgPhotos,
      handleGenerateDishImages,
      handleGenerateRecipes,
      imgBatchDoneCallback, recipeBatchDoneCallback, enrichBatchDoneCallback,
      setCurrentRestaurantId,
      activeBatches, registerBatch, dismissBatch,
    }}>
      {children}
    </ImageGenerationContext.Provider>
  );
}

export function useImageGeneration() {
  const context = useContext(ImageGenerationContext);
  if (!context) throw new Error("useImageGeneration must be used within ImageGenerationProvider");
  return context;
}
